import { faClose, faEye, faGear, faTrash } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box, Button, Tooltip, Typography } from "@mui/material"
import { translate } from "app/language/service"
import { IRootState } from "app/model"
import {
  deleteAllNotifications,
  markAllNotificationsAsRead,
  setNotificationSliderVisibility
} from "notification/tools/common"
import React from "react"
import { connect } from "react-redux"
import { FlexBox } from "ui/components/FlexBox"
import { MENU_CLOSED_WIDTH_IN_REM } from "ui/model"
import { NotificationList } from "./NotificationList"

interface INotificationSliderProps {
  isOpened: boolean
}

const SLIDER_WIDTH_IN_REM = "30rem"
const NOTIFICATION_HEADER_HEIGHT = "6rem"

export const NotificationSliderFn = React.memo(
  ({ isOpened }: INotificationSliderProps): JSX.Element => (
    <FlexBox
      vertical
      sx={{
        position: "fixed",
        top: 0,
        left: isOpened ? MENU_CLOSED_WIDTH_IN_REM : `-${SLIDER_WIDTH_IN_REM}`,
        width: SLIDER_WIDTH_IN_REM,
        minWidth: SLIDER_WIDTH_IN_REM,
        maxWidth: SLIDER_WIDTH_IN_REM,
        height: "100%",
        minWeight: "100%",
        minHeight: "100%",
        transition: "all 400ms ease",
        boxShadow: theme => theme.palette.boxShadow,
        background: theme => theme.palette.background.default,
        zIndex: 10
      }}>
      <FlexBox
        vertical
        fullWidth
        sx={{
          "height": NOTIFICATION_HEADER_HEIGHT,
          "minHeight": NOTIFICATION_HEADER_HEIGHT,
          "maxHeight": NOTIFICATION_HEADER_HEIGHT,
          "p": "1rem",
          "pb": ".5rem",
          "borderBottom": theme => theme.palette.border,
          "& .icon": {
            cursor: "pointer",
            color: theme => theme.palette.text.primary2,
            fontSize: "1.8rem"
          },
          "& .icon-close": {
            "fontSize": "1.5rem",
            "&:hover": {
              color: theme => theme.palette.primary.main
            }
          }
        }}>
        <FlexBox fullWidth jc="space-between">
          <FlexBox gap fullHeight>
            <FontAwesomeIcon icon={faGear} className="icon" />
            <Typography fontSize="1.2rem" fontWeight="bold" color="textPrimary">
              {translate("label.notifications")}
            </Typography>
          </FlexBox>
          <Tooltip title={translate("notification.hide")}>
            <span>
              <FontAwesomeIcon
                icon={faClose}
                className="icon icon-close"
                onClick={() => setNotificationSliderVisibility(false)}
              />
            </span>
          </Tooltip>
        </FlexBox>
        <FlexBox fullWidth gap jc="end">
          <Button variant="text" onClick={deleteAllNotifications} startIcon={<FontAwesomeIcon icon={faTrash} />}>
            {translate("notification.clear.all")}
          </Button>
          <Button variant="text" onClick={markAllNotificationsAsRead} startIcon={<FontAwesomeIcon icon={faEye} />}>
            {translate("notification.mark.all.as.read")}
          </Button>
        </FlexBox>
      </FlexBox>
      <FlexBox fullWidth fullHeight={NOTIFICATION_HEADER_HEIGHT}>
        <NotificationList />
      </FlexBox>
      {isOpened && (
        <Box
          onClick={() => setNotificationSliderVisibility(false)}
          sx={{
            position: "fixed",
            top: 0,
            left: `calc(${MENU_CLOSED_WIDTH_IN_REM} + ${SLIDER_WIDTH_IN_REM})`,
            width: `calc(100% - ${SLIDER_WIDTH_IN_REM})`,
            minWidth: `calc(100% - ${SLIDER_WIDTH_IN_REM})`,
            maxWidth: `calc(100% - ${SLIDER_WIDTH_IN_REM})`,
            height: "calc(100%)",
            minHeight: "calc(100%)",
            maxHeight: "calc(100%)"
          }}
        />
      )}
    </FlexBox>
  )
)

export const NotificationSlider = connect(
  (state: IRootState): INotificationSliderProps => ({
    isOpened: state.notification.isSliderOpened
  })
)(NotificationSliderFn)
